import { CodeableConcept, Condition } from "@medplum/fhirtypes";

import {
  ProblemType,
  QuiltedHealthValueSets,
  ResourceClientVisibility,
} from "@/fhir/enums/quilted-health";

import { ProceduresPickerSystems } from "../constants/procedures";

/**
 * What puts a Condition on the client's problem list.
 *
 * Membership is not a resource of its own: every reader searches for this code on `Condition.category`
 * — the client side panel, the PAM list, the client export — and none of them filters on the system,
 * so a membership written by PAM under `condition-clinical` and one written by the encounter bot under
 * `condition-category` are both found. Recognising it by code alone is therefore the only check that
 * agrees with what the readers actually do.
 */
export const PROBLEM_LIST_ITEM_CODE = "problem-list-item";

/**
 * The categories that qualify a problem-list membership rather than the encounter's own record of the
 * diagnosis: whether the client sees it on their panel, and whether it counts as a pregnancy problem.
 *
 * They travel with the membership because they are decisions somebody made about the client's problem
 * list — in PAM, or on the encounter answers form — not facts about this encounter.
 */
const PROBLEM_LIST_FACET_SYSTEMS: string[] = [
  QuiltedHealthValueSets.ProblemType,
  QuiltedHealthValueSets.ResourceClientVisibility,
];

const isProblemListFacet = (category: CodeableConcept): boolean =>
  Boolean(
    category.coding?.some(
      ({ system, code }) =>
        code === PROBLEM_LIST_ITEM_CODE || (system && PROBLEM_LIST_FACET_SYSTEMS.includes(system)),
    ),
  );

/** Whether this diagnosis is on the client's problem list. */
export const isProblemListDiagnosis = (condition: Condition | undefined): boolean =>
  Boolean(
    condition?.category?.some((category) =>
      category.coding?.some(({ code }) => code === PROBLEM_LIST_ITEM_CODE),
    ),
  );

/**
 * The problem-list categories a write has to hand back to a diagnosis it rewrites.
 *
 * Carried over verbatim rather than rebuilt: the visibility and problem-type facets record what
 * somebody chose for the client's problem list, and re-minting them would quietly overrule a
 * diagnosis hidden from the client panel or marked as a pregnancy problem.
 */
export const toCarriedProblemListCategories = (
  condition: Condition | undefined,
): CodeableConcept[] =>
  isProblemListDiagnosis(condition) ? (condition?.category ?? []).filter(isProblemListFacet) : [];

/**
 * The same diagnosis, off the client's problem list.
 *
 * Used when a diagnosis is retired. The problem-list searches filter on category alone, with no
 * verification-status filter, so a retired diagnosis that kept these categories would stay on the
 * client's problem list for good.
 */
export const withoutProblemListCategories = <T extends Condition>(condition: T): T => {
  if (!condition.category?.length) return condition;

  const category = condition.category.filter((entry) => !isProblemListFacet(entry));

  // FHIR arrays are never empty, so a diagnosis whose only categories were the membership loses the
  // element rather than keeping an empty one.
  if (!category.length) {
    const { category: _emptied, ...withoutCategory } = condition;
    return withoutCategory as T;
  }

  return { ...condition, category };
};

/**
 * The categories that put a diagnosis on the client's problem list.
 *
 * The same three the encounter bot writes for the answers form's `Add to Problem List`, so a
 * membership added from the Dx tab is indistinguishable from one added there: visible on the client's
 * panel, and counted as a non-pregnancy problem. A provider who wants it hidden or counted as a
 * pregnancy problem changes that in PAM, and {@link toCarriedProblemListCategories} keeps the choice.
 */
export const buildProblemListCategories = (): CodeableConcept[] => [
  {
    coding: [{ system: ProceduresPickerSystems.ConditionCategory, code: PROBLEM_LIST_ITEM_CODE }],
  },
  {
    coding: [
      {
        system: QuiltedHealthValueSets.ResourceClientVisibility,
        code: ResourceClientVisibility.Visible,
      },
    ],
  },
  {
    coding: [{ system: QuiltedHealthValueSets.ProblemType, code: ProblemType.NonPregnancyProblem }],
  },
];

/**
 * The same diagnosis, on or off the client's problem list.
 *
 * Adding strips whatever membership was there before rather than appending to it, so toggling a
 * diagnosis on twice cannot leave it carrying the categories twice over.
 */
export const withProblemListMembership = <T extends Condition>(
  condition: T,
  isMember: boolean,
): T => {
  const withoutMembership = withoutProblemListCategories(condition);

  if (!isMember) return withoutMembership;

  return {
    ...withoutMembership,
    category: [...(withoutMembership.category ?? []), ...buildProblemListCategories()],
  };
};
